import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraCalendarHeat, SpecraSectionHeader, SpecraSegmentedControl, SpecraStackedBarList, SpecraText, SpecraTrendChart } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { calendarView, GROUP_NOUN, lateColumns, STATUS_SEGMENTS, statusRows, trendPoints } from './charts';
// SAD-1000: Delivery Health, the Command Center's three charts. They read
// the summary the page already has (no request of their own), follow the
// global filters, and every point, bar and cell opens its rows.
const GROUP_OPTIONS = [
    {
        value: 'program',
        label: 'Program'
    },
    {
        value: 'team',
        label: 'Team'
    },
    {
        value: 'owner',
        label: 'Owner'
    }
];
export function DeliveryHealth({ summary, filter }) {
    const hasTeams = (summary?.workstreams ?? []).some((w)=>w.workstreamId);
    const [chosen, setChosen] = useState(null);
    // Teams first, as the design reads; a portfolio with no teams yet starts on programs.
    const by = chosen ?? (hasTeams || !summary ? 'team' : 'program');
    const points = useMemo(()=>trendPoints(summary, filter), [
        summary,
        filter
    ]);
    const late = useMemo(()=>lateColumns(summary, filter), [
        summary,
        filter
    ]);
    const status = useMemo(()=>statusRows(summary, by, filter), [
        summary,
        by,
        filter
    ]);
    const calendar = useMemo(()=>calendarView(summary, filter), [
        summary,
        filter
    ]);
    const onTime = summary?.kpis.find((k)=>k.id === 'on_time');
    const target = Number.parseInt(onTime?.target ?? '100', 10);
    const [one, many] = GROUP_NOUN[by];
    const periodWords = filter.period === 'all' ? 'All dates' : 'Current period';
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2",
        "data-testid": Testids.commandCenter.health,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-baseline justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        headingAs: "h2",
                        title: "Delivery Health",
                        variant: "group"
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: "Click any point, bar or day to list its deliverables"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "grid gap-4 lg:grid-cols-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraTrendChart, {
                        className: "lg:col-start-1",
                        columns: {
                            label: 'Late at month end',
                            items: late
                        },
                        "data-testid": Testids.commandCenter.healthTrend,
                        description: "Last 12 months · by month due · the same rule as the on-time tile",
                        emptyMessage: "Nothing came due in the last 12 months, so there is no on-time rate yet. It starts counting at the first approval or the first overdue deliverable.",
                        getColumnTestId: ()=>Testids.commandCenter.healthLateBar,
                        getPointTestId: ()=>Testids.commandCenter.healthPoint,
                        labelEvery: 2,
                        points: points,
                        renderLink: renderRouterLink,
                        seriesLabel: "On-time delivery",
                        target: Number.isFinite(target) ? {
                            value: target,
                            label: `Target: ${target}%`
                        } : undefined,
                        title: "On-time rate by month"
                    }),
                    /*#__PURE__*/ _jsx(SpecraStackedBarList, {
                        actions: /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                            label: "Group by",
                            onValueChange: setChosen,
                            options: GROUP_OPTIONS,
                            size: "sm",
                            testId: Testids.commandCenter.healthGroupBy,
                            value: by
                        }),
                        className: "lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-start",
                        "data-testid": Testids.commandCenter.healthStatus,
                        description: `${periodWords} · worst first, by share late or at risk`,
                        emptyMessage: by === 'team' ? 'No teams have deliverables in this view. Teams come from SOW intake.' : `No ${many} have deliverables in this view.`,
                        folded: status.quiet,
                        foldedLabel: (count)=>`+ ${count} ${count === 1 ? one : many} with nothing late or at risk`,
                        getRowTestId: ()=>Testids.commandCenter.healthRow,
                        getSegmentTestId: ()=>Testids.commandCenter.healthSegment,
                        renderLink: renderRouterLink,
                        rows: status.rows,
                        segments: STATUS_SEGMENTS,
                        title: `Status by ${one}`,
                        valueLabel: "Late or at risk"
                    }),
                    /*#__PURE__*/ _jsx(SpecraCalendarHeat, {
                        className: "lg:col-start-1",
                        "data-testid": Testids.commandCenter.healthCalendar,
                        description: "Deliverables late or still owed, per day · this week and the next five",
                        emptyMessage: "Nothing is late or falls due in these six weeks.",
                        getDayTestId: ()=>Testids.commandCenter.healthDay,
                        insight: calendar.busiest ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                "The week of ",
                                /*#__PURE__*/ _jsx("b", {
                                    children: calendar.busiest.label
                                }),
                                " carries the most:",
                                ' ',
                                calendar.busiest.count.toLocaleString(),
                                ' ',
                                calendar.busiest.count === 1 ? 'deliverable' : 'deliverables',
                                "."
                            ]
                        }) : undefined,
                        renderLink: renderRouterLink,
                        title: "Due-date calendar",
                        unit: "due",
                        weekdays: calendar.weekdays,
                        weeks: calendar.weeks
                    })
                ]
            })
        ]
    });
}
